import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import AISparklesAnimation from '@/components/animations/AISparklesAnimation';
import type { AiGenerationPhase } from '@/hooks/use-ai-generation-runner';

interface AISectionSkeletonProps {
    visible: boolean;
    sectionId?: string;
    /**
     * The run's phase, so the skeleton can dim once a stop is in flight.
     *
     * Reading only — the stop itself belongs to the panel that started the
     * generation. The laudo is the document, not a control surface, and a
     * button floating in the middle of a page being written is one more thing
     * to look at while waiting.
     */
    phase?: AiGenerationPhase;
}

type LoadingPhase = 'start' | 'middle' | 'end';
type LoadingMessageGroups = Record<LoadingPhase, readonly string[]>;

const MESSAGE_ROTATION_INTERVAL = 2800;

const AI_MESSAGE_GRADIENT_STYLE = {
    background: 'linear-gradient(118deg, #01026B 0%, #0E4CE8 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
} as const;

const GENERIC_AI_LOADING_MESSAGES: LoadingMessageGroups = {
    start: [
        'Reunindo informações disponíveis',
        'Preparando o contexto da seção',
        'Lendo os conteúdos já preenchidos',
        'Organizando as fontes clínicas',
        'Identificando dados relevantes',
        'Separando os pontos essenciais',
        'Mapeando informações importantes',
        'Preparando a base do texto',
        'Analisando o material disponível',
        'Localizando informações úteis para esta seção',
    ],
    middle: [
        'Cruzando informações relevantes',
        'Relacionando dados do caso',
        'Construindo uma linha de raciocínio',
        'Organizando os principais achados',
        'Analisando conexões importantes',
        'Refinando a interpretação clínica',
        'Compondo uma síntese técnica',
        'Transformando dados em texto clínico',
        'Verificando coerência entre as informações',
        'Estruturando os pontos mais importantes',
    ],
    end: [
        'Refinando a escrita da seção',
        'Ajustando a clareza do texto',
        'Consolidando os principais pontos',
        'Organizando a apresentação das ideias',
        'Preparando uma versão revisável',
        'Ajustando os últimos detalhes do texto',
        'Revisando a consistência da formulação',
        'Preparando o conteúdo gerado',
    ],
};

const SECTION_AI_LOADING_MESSAGES: Record<string, Partial<LoadingMessageGroups>> = {
    demand_description: {
        start: [
            'Identificando o motivo principal da avaliação',
            'Organizando a queixa apresentada',
            'Relacionando dados iniciais do caso',
        ],
        middle: [
            'Construindo uma formulação técnica da demanda',
            'Conectando queixa, contexto e objetivos',
            'Refinando a descrição da demanda',
        ],
        end: ['Preparando uma versão clara da demanda', 'Ajustando a formulação para revisão'],
    },
    behavior_observations: {
        start: [
            'Lendo as respostas da observação',
            'Organizando indicadores comportamentais',
            'Separando aspectos de interação e postura',
        ],
        middle: [
            'Identificando padrões observados',
            'Relacionando comportamento, interação e contexto',
            'Compondo a observação clínica',
        ],
        end: ['Refinando a descrição comportamental', 'Preparando a observação para revisão'],
    },
    clinical_history: {
        start: [
            'Reunindo informações da anamnese',
            'Organizando dados do desenvolvimento',
            'Mapeando eventos clínicos relevantes',
        ],
        middle: [
            'Relacionando histórico individual e familiar',
            'Construindo uma narrativa clínica coesa',
            'Refinando a linha temporal do caso',
        ],
        end: [
            'Ajustando a continuidade da narrativa',
            'Preparando a história clínica para revisão',
        ],
    },
    tests: {
        start: [
            'Lendo os dados do instrumento',
            'Organizando os escores informados',
            'Localizando indicadores relevantes',
        ],
        middle: [
            'Interpretando padrões nos resultados',
            'Relacionando indicadores do instrumento',
            'Consolidando a análise dos dados',
        ],
        end: ['Compondo uma interpretação técnica', 'Preparando a síntese dos resultados'],
    },
    diagnosis: {
        start: [
            'Reunindo evidências clínicas disponíveis',
            'Organizando elementos para a conclusão',
            'Separando dados relevantes para a hipótese clínica',
        ],
        middle: [
            'Relacionando história clínica e resultados',
            'Analisando hipóteses diagnósticas',
            'Construindo uma justificativa clínica',
        ],
        end: ['Refinando a síntese conclusiva', 'Preparando a conclusão para revisão'],
    },
    recommendations: {
        start: [
            'Reunindo pontos centrais do caso',
            'Identificando necessidades de acompanhamento',
            'Organizando dados para orientação',
        ],
        middle: [
            'Relacionando conclusão e encaminhamentos',
            'Organizando recomendações possíveis',
            'Construindo orientações técnicas',
        ],
        end: ['Refinando as recomendações finais', 'Preparando orientações para revisão'],
    },
};

const mixMessages = (genericMessages: readonly string[], sectionMessages?: readonly string[]) => {
    if (!sectionMessages?.length) return [...genericMessages];

    const mixedMessages: string[] = [];
    const maxLength = Math.max(genericMessages.length, sectionMessages.length);

    for (let index = 0; index < maxLength; index += 1) {
        if (genericMessages[index]) mixedMessages.push(genericMessages[index]);
        if (sectionMessages[index]) mixedMessages.push(sectionMessages[index]);
    }

    return mixedMessages;
};

const getLoadingMessageGroups = (sectionId?: string): LoadingMessageGroups => {
    const sectionMessages = sectionId ? SECTION_AI_LOADING_MESSAGES[sectionId] : undefined;

    return {
        start: mixMessages(GENERIC_AI_LOADING_MESSAGES.start, sectionMessages?.start),
        middle: mixMessages(GENERIC_AI_LOADING_MESSAGES.middle, sectionMessages?.middle),
        end: mixMessages(GENERIC_AI_LOADING_MESSAGES.end, sectionMessages?.end),
    };
};

const getLoadingPhase = (messageIndex: number): LoadingPhase => {
    if (messageIndex < 2) return 'start';
    if (messageIndex < 6) return 'middle';

    return (messageIndex - 6) % 3 === 1 ? 'middle' : 'end';
};

const getPhaseMessageIndex = (messageIndex: number, currentPhase: LoadingPhase) => {
    let phaseMessageIndex = 0;

    for (let index = 0; index < messageIndex; index += 1) {
        if (getLoadingPhase(index) === currentPhase) phaseMessageIndex += 1;
    }

    return phaseMessageIndex;
};

/**
 * Section-level skeleton shown inside a report section while AI generates content.
 * Replaces only the section content area — the section title remains visible.
 *
 * Design: matches the "Gerar com IA" button palette — deep navy (#01026B) to
 * bright blue (#0E4CE8) with white-ish radial highlights and the Lottie
 * sparkles animation.
 */
export function AISectionSkeleton({
    visible,
    sectionId,
    phase = 'running',
}: AISectionSkeletonProps) {
    const shouldReduceMotion = useReducedMotion();
    const isStopping = phase === 'cancelling';
    const [messageIndex, setMessageIndex] = useState(0);
    const loadingMessageGroups = useMemo(() => getLoadingMessageGroups(sectionId), [sectionId]);
    const currentPhase = getLoadingPhase(messageIndex);
    const phaseMessages = loadingMessageGroups[currentPhase];
    const currentMessage =
        phaseMessages[getPhaseMessageIndex(messageIndex, currentPhase) % phaseMessages.length];

    useEffect(() => {
        if (!visible || isStopping) return;

        const intervalId = window.setInterval(() => {
            setMessageIndex((current) => current + 1);
        }, MESSAGE_ROTATION_INTERVAL);

        return () => window.clearInterval(intervalId);
    }, [loadingMessageGroups, visible, isStopping]);

    // Kept apart from the rotation above so asking to stop does not restart the
    // copy from "Reunindo informações disponíveis" — which would read as the
    // generation starting over at the exact moment it was told to end.
    useEffect(() => {
        if (visible) setMessageIndex(0);
    }, [loadingMessageGroups, visible]);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="ai-section-skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                        duration: 0.35,
                        ease: [0.4, 0, 0.2, 1],
                    }}
                    className="relative rounded-md py-4"
                >
                    {/* Status indicator — Lottie sparkles + gradient label */}
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.25 }}
                        className="mb-4 flex items-start gap-1"
                    >
                        <AISparklesAnimation
                            loop={!isStopping}
                            className={`size-8 shrink-0 ${isStopping ? 'opacity-40' : ''}`}
                        />
                        <div className="pt-1">
                            <span className="block min-h-[18px] overflow-hidden text-[13px] font-medium leading-tight tracking-wide">
                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.span
                                        key={currentMessage}
                                        initial={
                                            shouldReduceMotion
                                                ? false
                                                : {
                                                      opacity: 0,
                                                      y: 6,
                                                      filter: 'blur(3px)',
                                                  }
                                        }
                                        animate={{
                                            opacity: 1,
                                            y: 0,
                                            filter: 'blur(0px)',
                                        }}
                                        exit={
                                            shouldReduceMotion
                                                ? { opacity: 0 }
                                                : {
                                                      opacity: 0,
                                                      y: -5,
                                                      filter: 'blur(3px)',
                                                  }
                                        }
                                        transition={
                                            shouldReduceMotion
                                                ? { duration: 0 }
                                                : {
                                                      duration: 0.32,
                                                      ease: [0.22, 1, 0.36, 1],
                                                  }
                                        }
                                        className="block"
                                        style={{
                                            ...AI_MESSAGE_GRADIENT_STYLE,
                                            willChange: 'opacity, transform, filter',
                                        }}
                                    >
                                        {currentMessage}
                                    </motion.span>
                                </AnimatePresence>
                            </span>
                            <p className="mt-1 max-w-[32rem] text-[12px] leading-relaxed text-slate-500">
                                A IA pode se equivocar. Revise o conteúdo e confirme se as
                                informações estão adequadas ao caso clínico.
                            </p>
                        </div>
                    </motion.div>

                    {/* Skeleton bars — shimmer lives inside each bar. They stay
                        on while stopping, faded: clearing them at the click
                        would promise an instant halt the worker cannot make
                        good on. */}
                    <div className={`space-y-2.5 ${isStopping ? 'opacity-45' : ''}`}>
                        {SKELETON_LINES.map((width, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{
                                    delay: 0.15 + i * 0.06,
                                    duration: 0.3,
                                }}
                                className="relative overflow-hidden rounded"
                                style={{
                                    height: 10,
                                    width,
                                    background:
                                        'linear-gradient(118deg, rgba(1,2,107,0.08) 0%, rgba(6,28,128,0.04) 22%, rgba(14,76,232,0.10) 48%, rgba(2,22,120,0.08) 68%, rgba(255,255,255,0.02) 100%)',
                                }}
                            >
                                {/* Per-bar shimmer sweep */}
                                <div
                                    className="absolute inset-0 rounded"
                                    style={{
                                        background:
                                            'linear-gradient(105deg, transparent 35%, rgba(107,164,255,0.14) 45%, rgba(6,28,128,0.06) 50%, rgba(107,164,255,0.14) 55%, transparent 65%)',
                                        backgroundSize: '200% 100%',
                                        animation: 'ai-bar-shimmer 4s ease-in-out infinite',
                                        animationDelay: `${i * 0.18}s`,
                                    }}
                                />
                            </motion.div>
                        ))}
                    </div>

                    {/* Inline keyframes */}
                    <style>{`
                        @keyframes ai-bar-shimmer {
                            0% { background-position: 200% 0; }
                            100% { background-position: -200% 0; }
                        }
                    `}</style>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/** Line widths to mimic paragraph text */
const SKELETON_LINES = ['100%', '100%', '92%', '100%', '85%', '100%', '70%'] as const;
